import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import CustomContent from '@arcgis/core/popup/content/CustomContent';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import MapView from '@arcgis/core/views/MapView';
import { DEFAULT_MAP_SOURCE } from '@env';

import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Injectable,
  Injector,
  Type,
} from '@angular/core';

import { IFsMap } from '@lib/geo/core';
import { isEsriMap } from '@lib/geo/helpers';
import { FsPopup, IFsMapOffset, IFsMapPopupOptions, MAP_POPUP_DATA, MapType } from '@lib/geo/map';

@Injectable({
  providedIn: 'root',
})
export class MapPopupService<TMap extends IFsMap> {
  get defaultMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  private componentRef: ComponentRef<any>;
  private mapPopup: FsPopup;
  private _map: TMap;

  constructor(
    private readonly resolver: ComponentFactoryResolver,
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
  ) {}

  public openPopup(component: HTMLElement, data: any, map: TMap): void {
    this._map = map;
    if (isEsriMap(map)) {
      this._openEsriPopup(data, (map as any).mapView, component);
    } else {
      this._openGooglePopup(
        data,
        (map as TMap & { mapInstance: MapType<'google'> }).mapInstance,
        component,
      );
    }
  }

  public closePopup(): void {
    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
    }

    this.defaultMapSource === 'google' ? this._closeGooglePopup() : this._closeEsriPopup();
  }

  public createPopupComponent(
    component: Type<any>,
    data: any,
  ): {
    componentRef: ComponentRef<any>;
    domElem: HTMLElement;
  } {
    const factory = this.resolver.resolveComponentFactory(component);
    const injector = Injector.create({
      providers: [{ provide: MAP_POPUP_DATA, useValue: data }],
      parent: this.injector,
    });
    this.componentRef = factory.create(injector);

    this.appRef.attachView(this.componentRef.hostView);
    const domElem = (this.componentRef.hostView as any).rootNodes[0] as HTMLElement;

    return { componentRef: this.componentRef, domElem };
  }

  private _createGooglePixelOffset(offset: IFsMapOffset): google.maps.Size {
    return new google.maps.Size(offset.width, offset.height, offset.unit, offset.unit);
  }

  private _openGooglePopup(
    data: IFsMapPopupOptions,
    map: MapType<'google'>,
    domElem: HTMLElement,
  ): void {
    const pixelOffset = data?.offset ? this._createGooglePixelOffset(data.offset) : undefined;
    const infowindow = new google.maps.InfoWindow({
      content: domElem,
      pixelOffset,
    });
    infowindow.setPosition({ lng: data.lonLat[0], lat: data.lonLat[1] });
    this.mapPopup = infowindow as unknown as FsPopup;
    infowindow.open(map);
  }

  private _openEsriPopup(data: IFsMapPopupOptions, map: MapView, domElem: HTMLElement): void {
    const location = new Point({
      latitude: data.lonLat[1],
      longitude: data.lonLat[0],
    });
    const content = new CustomContent({
      outFields: ['*'],
      creator: () => {
        return domElem;
      },
    });
    const popupTemplate = new PopupTemplate({
      title: data.data.title,
      content: [content],
    });

    const graphic = new Graphic({
      geometry: location,
      popupTemplate,
    });

    map.popup.dockEnabled = false;
    map.popup.dockOptions = {
      buttonEnabled: false,
      breakpoint: false,
    };
    map.popup.autoOpenEnabled = false; // Disable auto-open/close behavior

    // Explicitly open the popup at the specified location
    map.popup.open({
      features: [graphic],
      location: location,
      updateLocationEnabled: true,
    });

    this.mapPopup = map.popup as unknown as FsPopup;
  }

  private _closeGooglePopup(): void {
    this.mapPopup.close();
    this.mapPopup = null;
  }

  private _closeEsriPopup(): void {
    (this._map as TMap & { mapView: { popup: { close: () => void } } }).mapView.popup.close();
    this.mapPopup = null;
  }
}
